import { useMemo, useState } from 'react';
import { api } from '../api';
import {
  APPOINTMENT_EVENT_LABELS,
  APPOINTMENT_OUTCOMES,
  APPOINTMENT_OUTCOME_COLORS,
  APPOINTMENT_OUTCOME_LABELS,
  type AppointmentFilterState,
  type AppointmentOutcome,
  type AppointmentSummaryResponse,
  type AppointmentsResponse,
  type LocationSummary,
} from '../types';
import { formatCompact, formatDateTime, formatDuration, formatNumber, formatPercent } from '../format';
import { StatTile } from '../components/StatTile';
import { StackedColumnChart } from '../components/StackedColumnChart';
import { HorizontalBarChart, type BarDatum } from '../components/HorizontalBarChart';
import { ContactJourneyDrawer } from '../components/ContactJourneyDrawer';
import { AppointmentJourneyDrawer } from '../components/AppointmentJourneyDrawer';

/** Stack order is the validated palette order — see types.ts. */
const APPOINTMENT_SERIES = APPOINTMENT_OUTCOMES.map((outcome) => ({
  key: outcome,
  label: APPOINTMENT_OUTCOME_LABELS[outcome],
  color: APPOINTMENT_OUTCOME_COLORS[outcome],
}));

function StatusTag({ outcome, detail }: { outcome: AppointmentOutcome; detail?: string | null }) {
  return (
    <span className="tag" title={detail ?? undefined}>
      <span className="dot" style={{ background: APPOINTMENT_OUTCOME_COLORS[outcome] }} aria-hidden="true" />
      {APPOINTMENT_OUTCOME_LABELS[outcome]}
    </span>
  );
}

/** Raw statuses the bucket label already conveys. */
const REDUNDANT = new Set(['showed', 'noshow', 'cancelled', 'canceled', 'invalid', '']);

function rawStatusDetail(status: string | null | undefined): string | null {
  const value = (status ?? '').toLowerCase();
  return REDUNDANT.has(value) ? null : (status ?? null);
}

interface Props {
  summary: AppointmentSummaryResponse | null;
  list: AppointmentsResponse | null;
  loading: boolean;
  locations: LocationSummary[];
  filters: AppointmentFilterState;
  onFiltersChange: (next: AppointmentFilterState) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

function relativeChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / previous;
}

export function Appointments({
  summary,
  list,
  loading,
  locations,
  filters,
  onFiltersChange,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: Props) {
  const [showTable, setShowTable] = useState(true);
  const [journeyContactId, setJourneyContactId] = useState<string | null>(null);
  const [journeyApptId, setJourneyApptId] = useState<string | null>(null);

  const locationBars = useMemo<BarDatum[]>(() => {
    if (!summary) return [];
    return summary.byLocation.slice(0, 12).map((row) => ({
      key: row.locationId,
      label: row.locationName ?? row.locationId,
      value: row.total,
      tooltipRows: [
        { label: 'Appointments', value: formatNumber(row.total) },
        { label: 'Showed', value: formatNumber(row.showed) },
        { label: 'No show', value: formatNumber(row.noshow) },
        { label: 'Cancelled', value: formatNumber(row.cancelled) },
      ],
    }));
  }, [summary]);

  const userBars = useMemo<BarDatum[]>(() => {
    if (!summary) return [];
    const rows = summary.byUser.filter((row) => row.userId).slice(0, 12);
    const nameCounts = new Map<string, number>();
    for (const row of rows) {
      const name = row.userName ?? row.userId!;
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    }
    return rows.map((row) => {
      const name = row.userName ?? row.userId!;
      const ambiguous = (nameCounts.get(name) ?? 0) > 1;
      return {
        key: `${row.userId}-${row.locationId}`,
        label: ambiguous && row.locationName ? `${name} · ${row.locationName}` : name,
        sublabel: row.locationName ?? undefined,
        value: row.total,
        tooltipRows: [
          { label: 'Appointments', value: formatNumber(row.total) },
          { label: 'Showed', value: formatNumber(row.showed) },
          { label: 'No show', value: formatNumber(row.noshow) },
          { label: 'Cancelled', value: formatNumber(row.cancelled) },
        ],
      };
    });
  }, [summary]);

  if (!summary) {
    return <p className="empty">{loading ? 'Loading appointments…' : 'No appointment data yet — run a sync.'}</p>;
  }

  const { totals, previous } = summary;
  const movement = summary.movement ?? [];
  const movementTotal = movement.reduce((sum, entry) => sum + entry.count, 0);
  const reschedules = movement.find((entry) => entry.event_type === 'rescheduled')?.count ?? 0;
  const bookedChange = relativeChange(totals.appointments, previous.appointments);
  const showedChange = relativeChange(totals.showed, previous.showed);
  const countingBy = filters.dateBasis === 'booked' ? 'date booked' : 'appointment date';
  const personHeading = filters.userBasis === 'assigned' ? 'Who they are assigned to' : 'Who booked them';
  const totalPages = list ? Math.max(1, Math.ceil(list.total / pageSize)) : 1;

  return (
    <div className={loading ? 'stack reloading' : 'stack'}>
      <div className="grid grid-kpi">
        <StatTile
          label="Appointments"
          value={formatCompact(totals.appointments)}
          hero
          delta={bookedChange === null ? null : { change: bookedChange, higherIsBetter: true }}
        />
        <StatTile
          label="Showed"
          value={formatCompact(totals.showed)}
          delta={showedChange === null ? null : { change: showedChange, higherIsBetter: true }}
          footnote={
            totals.resolved > 0
              ? `of ${formatNumber(totals.resolved)} resolved`
              : 'No appointments have resolved yet'
          }
        />
        <StatTile
          label="No shows"
          value={formatCompact(totals.noshow)}
          footnote={`${formatNumber(totals.booked)} still upcoming`}
        />
        <StatTile
          label="Cancelled"
          value={formatCompact(totals.cancelled)}
          footnote={`${formatPercent(totals.cancelRate, 1)} of appointments`}
        />
        <StatTile
          label="Booked time"
          value={formatDuration(totals.bookedMinutes * 60)}
          footnote={`${formatNumber(totals.calendars)} calendars in use`}
        />
        <StatTile
          label="Self-booked"
          compact
          value={`${formatCompact(totals.selfBooked)} of ${formatCompact(totals.appointments)}`}
          footnote="Booked by a contact, not a staff member"
        />
        <StatTile
          label="Rescheduled"
          value={formatCompact(reschedules)}
          footnote={
            movementTotal > 0
              ? `${formatNumber(movementTotal)} calendar changes recorded`
              : 'No changes recorded in this window yet'
          }
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Appointments per day by status</h2>
          <button type="button" className="btn btn-sm" onClick={() => setShowTable((value) => !value)}>
            {showTable ? 'Hide table' : 'Show table'}
          </button>
        </div>
        <p className="card-sub">
          Counted by <strong>{countingBy}</strong>. “Booked” means still upcoming — it has not had the
          chance to show or no-show yet.
        </p>
        <StackedColumnChart
          data={summary.timeseries}
          series={APPOINTMENT_SERIES}
          ariaLabel="Appointments per day by status"
        />
      </div>

      <div className="grid grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Appointments by sub-account</h2>
            <span className="small muted">{summary.byLocation.length} with activity</span>
          </div>
          <p className="card-sub">Click a bar to filter this page to that sub-account.</p>
          <HorizontalBarChart
            data={locationBars}
            onSelect={(locationId) =>
              onFiltersChange({ ...filters, locationIds: [locationId], userIds: [], calendarIds: [] })
            }
          />
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{personHeading}</h2>
            <span className="small muted">Top 12</span>
          </div>
          <p className="card-sub">
            {filters.userBasis === 'booker'
              ? `${formatNumber(totals.selfBooked)} appointments carry no booking user — those were booked by the contact through a calendar widget, funnel or workflow.`
              : 'The staff member the appointment belongs to.'}
          </p>
          <HorizontalBarChart data={userBars} labelGutter={210} />
        </div>
      </div>

      {showTable ? (
        <div className="card">
          <div className="card-head">
            <h2>Sub-account breakdown</h2>
            <span className="small muted">The table behind the charts</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Sub-account</th>
                  <th className="num">Appointments</th>
                  <th className="num">Showed</th>
                  <th className="num">No show</th>
                  <th className="num">Booked</th>
                  <th className="num">Cancelled</th>
                  <th className="num">Calendars</th>
                </tr>
              </thead>
              <tbody>
                {summary.byLocation.map((row) => {
                  return (
                    <tr key={row.locationId}>
                      <td className="nowrap">
                        <button
                          type="button"
                          className="btn btn-sm nowrap"
                          onClick={() =>
                            onFiltersChange({
                              ...filters,
                              locationIds: [row.locationId],
                              userIds: [],
                              calendarIds: [],
                            })
                          }
                        >
                          {row.locationName ?? row.locationId}
                        </button>
                      </td>
                      <td className="num cell-strong">{formatNumber(row.total)}</td>
                      <td className="num">{formatNumber(row.showed)}</td>
                      <td className="num">{formatNumber(row.noshow)}</td>
                      <td className="num">{formatNumber(row.booked)}</td>
                      <td className="num">{formatNumber(row.cancelled)}</td>
                      <td className="num">{formatNumber(row.calendars)}</td>
                    </tr>
                  );
                })}
                {summary.byLocation.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty">
                      No appointments in this range.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2>Appointment log</h2>
          <div className="row">
            <span className="small muted">{list ? `${formatNumber(list.total)} appointments` : '—'}</span>
            <a className="btn btn-sm" href={api.appointmentsCsvUrl(filters)} download>
              Export CSV
            </a>
          </div>
        </div>
        <p className="card-sub">Contact names link straight to the contact record in HighLevel.</p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Appointment</th>
                <th>Booked</th>
                <th>Sub-account</th>
                <th>Contact</th>
                <th>Booked by</th>
                <th>Assigned to</th>
                <th>Calendar</th>
                <th>Status</th>
                <th className="num">Length</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(list?.appointments ?? []).map((appointment) => (
                <tr key={appointment.id}>
                  <td className="nowrap">
                    {formatDateTime(appointment.startTime)}
                    {appointment.title ? <div className="cell-sub">{appointment.title}</div> : null}
                  </td>
                  <td className="nowrap">{formatDateTime(appointment.bookedAt)}</td>
                  <td>{appointment.locationName ?? appointment.locationId}</td>
                  <td>
                    {appointment.contactId ? (
                      <button
                        type="button"
                        className="link-button cell-strong"
                        onClick={() => setJourneyContactId(appointment.contactId)}
                        title="Open this contact's journey"
                      >
                        {appointment.contactName ?? appointment.contactPhone ?? 'Open contact'}
                      </button>
                    ) : (
                      <span className="cell-strong">
                        {appointment.contactName ?? appointment.contactPhone ?? '—'}
                      </span>
                    )}
                    <div className="cell-sub">
                      {appointment.contactPhone && appointment.contactName
                        ? `${appointment.contactPhone} · `
                        : ''}
                      {appointment.contactUrl ? (
                        <a href={appointment.contactUrl} target="_blank" rel="noreferrer">
                          HighLevel
                        </a>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    {appointment.bookedByName ?? (
                      <span className="muted">
                        {appointment.bookedBySource ? 'Self-booked' : '—'}
                      </span>
                    )}
                    {!appointment.bookedByName && appointment.bookedBySource ? (
                      <div className="cell-sub">{appointment.bookedBySource}</div>
                    ) : null}
                  </td>
                  <td>{appointment.assignedName ?? <span className="muted">Unassigned</span>}</td>
                  <td>{appointment.calendarName ?? '—'}</td>
                  <td>
                    <StatusTag outcome={appointment.outcome} detail={appointment.appointmentStatus} />
                    {rawStatusDetail(appointment.appointmentStatus) ? (
                      <div className="cell-sub">{rawStatusDetail(appointment.appointmentStatus)}</div>
                    ) : null}
                  </td>
                  <td className="num">
                    {appointment.durationMinutes ? `${appointment.durationMinutes}m` : '—'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setJourneyApptId(appointment.id)}
                    >
                      Journey
                    </button>
                  </td>
                </tr>
              ))}
              {list && list.appointments.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty">
                    No appointments match these filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <div className="row">
            <label htmlFor="appt-page-size" className="muted">
              Rows
            </label>
            <select
              id="appt-page-size"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {[25, 50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {movementTotal > 0 ? (
        <div className="card">
          <h2>Calendar movement</h2>
          <p className="card-sub">
            Changes recorded in this window across every matching sub-account — what a snapshot of
            statuses cannot show.
          </p>
          <div className="chips">
            {movement.map((entry) => (
              <span className="chip" key={entry.event_type}>
                {APPOINTMENT_EVENT_LABELS[entry.event_type] ?? entry.event_type}:{' '}
                <strong>{formatNumber(entry.count)}</strong>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {journeyApptId ? (
        <AppointmentJourneyDrawer appointmentId={journeyApptId} onClose={() => setJourneyApptId(null)} />
      ) : null}
      {journeyContactId ? (
        <ContactJourneyDrawer contactId={journeyContactId} onClose={() => setJourneyContactId(null)} />
      ) : null}
    </div>
  );
}